/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import { PillBottle, Search, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { searchDrugs } from '@/lib/prescriptionExtras';

/**
 * Autocomplete input backed by /api/drugs/search (AIFA-inspired seed).
 * Selecting a suggestion fills BOTH the name and AIC in the calling form.
 *
 * Props
 *   value        current text
 *   onChange     (name) => void   free-text change
 *   onSelect     (drug) => void   full drug picked from list (name+aic+active)
 *   placeholder
 *   testId
 */
export const DrugAutocomplete = ({ value = '', onChange, onSelect, placeholder = 'Nome commerciale o principio attivo', testId = 'drug-autocomplete' }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(0);
  const anchorRef = useRef(null);
  const debounceRef = useRef(null);

  const runSearch = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await searchDrugs(q, 12);
      setItems(res.items || []);
      setHover(0);
    } catch (e) {
      setItems([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.trim().length < 2) {
      setItems([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      runSearch(value.trim());
      setOpen(true);
    }, 180);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [value, runSearch]);

  const pick = (d) => {
    onSelect?.(d);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHover((h) => Math.min(h + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHover((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(items[hover]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <Popover open={open && items.length > 0} onOpenChange={(v) => !v && setOpen(false)}>
      <PopoverAnchor asChild>
        <div className="relative" ref={anchorRef}>
          <Input
            value={value}
            onChange={(e) => onChange?.(e.target.value.slice(0, 200))}
            onKeyDown={onKeyDown}
            onFocus={() => { if (items.length > 0) setOpen(true); }}
            placeholder={placeholder}
            className="pr-8"
            data-testid={testId}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          </div>
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 max-h-72 overflow-y-auto"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {items.map((d, i) => (
          <button
            key={d.aic}
            type="button"
            className={cn(
              'w-full text-left px-3 py-2 flex items-start gap-2 border-b border-border last:border-0',
              i === hover ? 'bg-primary/10' : 'hover:bg-muted',
            )}
            onMouseEnter={() => setHover(i)}
            onClick={() => pick(d)}
            data-testid={`${testId}-item-${d.aic}`}
          >
            <PillBottle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-sm truncate">{d.name}</div>
              <div className="text-[10.5px] text-muted-foreground truncate">
                {d.active} · AIC {d.aic}
                {d.ssn && <Badge variant="outline" className="ml-1 text-[9px]">SSN</Badge>}
              </div>
            </div>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

export default DrugAutocomplete;
